/**
 *
 * GroupDetails
 *
 */

import React, {
  memo,
  FunctionComponent,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';
import {
  accentColorPrim,
  fontColorSecond,
  red,
  white,
} from '../../../../assets/styledAssets/vars';
import { StyledTable, Wrap } from '../../../../assets/styledAssets/grid';
import {
  AddUserIcon, BackIcon,
  CloseIcon,
  PencilIcon,
  ReportIcon,
} from '../../../../components/Icons/Icons.component';
import { StyledButton } from './styles';
import AddMember from '../AddMember/AddMember.component';
import DialogWrap from '../../../../components/DiaogWrap/DiaogWrap.component';
import Button from '../../../../components/Button/Button.component';
import { GridColumn } from '@progress/kendo-react-grid';
import { StyledTD } from '../../../Questionnaires/QuestionnairesList/styles';
import Moment from 'react-moment';
import { Gender, User } from '../../../../domains/app';
import { Group } from '../../groups';
import debounce from 'lodash/debounce';
import {
  addMemberToGroup,
  fetchListMembers,
  removeMemberFromGroup,
} from '../../../../service/Groups.service';
import { SpaceMember } from '../../../Spaces/spaces';
import { forkJoin } from 'rxjs';
import Tooltip from '@material-ui/core/Tooltip';
import SendQuestionnaires from '../SendQuestionnaires/SendQuestionnaires.component';
import { Questionnaire } from '../../../Questionnaires/questionnaires';
import { sendQuestionnaire } from '../../../../service/Questionnarie.service';
import { SubcriptionPlan } from '../../../Settings/settings';

const pageSize = 50;

interface IProps {
  group: Group;
  showGroupDetails: (g: Group) => void;
  profile: User | null;
  subscriptionPlan?: SubcriptionPlan;
  onDetailsHide?: any
}

const GroupDetails: FunctionComponent<IProps> = ({
  group,
  showGroupDetails,
  profile,
  subscriptionPlan,
  onDetailsHide
}) => {
  const intl = useIntl();
  const [addMembers, setAddMembersDialog] = useState(false);
  const [groupMembers, setGroupMembers] = useState<User[]>([]);
  const [skip, setSkip] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [showSend, setShowSend] = useState<boolean>(false);

  useEffect(() => {
    if (group.id) {
      setGroupMembers([]);
      setPage(() => 0);
      setIsFetching(true);
      fetchListMembers(group.id as string, pageSize, 0)
        .then((res) => {
          setGroupMembers((gm) => gm.concat(res.items));
          setTotal(res.total);
          setPage((p) => p + 1);
        })
        .finally(() => setIsFetching(false));
    }
    // eslint-disable-next-line
  }, [group.id]);

  const fetch = useMemo(
    () =>
      debounce(() => {
        setIsFetching(true);
        fetchListMembers(group.id as string, pageSize, page)
          .then((res) => {
            setGroupMembers(groupMembers.concat(res.items));
            setTotal(res.total);
            setPage((p) => p + 1);
          })
          .finally(() => setIsFetching(false));
      }, 200),
    // eslint-disable-next-line
    [group, groupMembers, page, total],
  );

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= groupMembers.length &&
        !isFetching &&
        total > groupMembers.length
      ) {
        fetch();
      }
    },
    [isFetching, groupMembers.length, total, fetch, setSkip, skip],
  );

  const handleAddMembers = (listMembers: SpaceMember[]) => {
    let listRequests: Promise<any>[] = [];
    listMembers.forEach((m) =>
      listRequests.push(addMemberToGroup(group.id as string, m.personId)),
    );
    forkJoin(listRequests).subscribe(() => {
      setAddMembersDialog(false);
      setGroupMembers([]);
      setPage(() => 0);
      setIsFetching(true);
      fetchListMembers(group.id as string, pageSize, 0)
        .then((res) => {
          setGroupMembers((gm) => gm.concat(res.items));
          setTotal(res.total);
          setPage((p) => p + 1);
        })
        .finally(() => setIsFetching(false));
    });
  };

  const handleRemoveMember = (personId: string) => {
    removeMemberFromGroup(group.id as string, personId).then(() => {
      setGroupMembers(groupMembers.filter((m) => m.id !== personId));
    });
  };

  const updateField = (dataItem: any, field: keyof SpaceMember) => {
    const index = groupMembers.findIndex((m) => m.id === dataItem.id);
    let newMembers = [...groupMembers];
    newMembers[index] = {
      ...newMembers[index],
      [field]: !dataItem[field],
    };
    setGroupMembers(newMembers);
  };

  const sendQuestionnaires = (questionnaires: Questionnaire[]) => {
    const listRequests: Promise<Questionnaire>[] = [];
    const selectedMemberIds = groupMembers
      .filter((m) => m.checked)
      .map((m) => m.id) as string[];
    questionnaires.forEach((q) => {
      listRequests.push(
        sendQuestionnaire(
          q?.id as string,
          group.spaceId,
          profile?.id as string,
          selectedMemberIds,
        ),
      );
    });
    Promise.all(listRequests).then(() => {
      setShowSend(false);
    });
  };

  return (
    <>
      <Wrap
        width={'100%'}
        bgColor={white}
        overflow={['auto', 'unset']}
        padding={'5rem 3rem 0 3rem'}
        grow={'1'}
        flexDir={'column'}
        height={'100%'}
      >
        <Wrap
          justifyCont={'space-between'}
          width={'100%'}
          alignItems={'center'}
        >
          <Wrap alignItems={'center'}>
            <Wrap hiddenSM margin={'0 1.2rem 0 0'}>
              <StyledButton icon onClick={onDetailsHide}>
                <BackIcon color={accentColorPrim} width={'1.8rem'} />
              </StyledButton>
            </Wrap>
            <Wrap alignItems={'flex-start'}
                  flexDir={'column'}>
              <Wrap
                fs={'3.2rem'}
                fw={'bold'}
                color={accentColorPrim}
                alignItems={'flex-end'}
                margin={'0 0 1rem 0'}
              >
                {group.name}
              </Wrap>
              <Wrap fs={'1.6rem'} color={fontColorSecond} alignItems={'flex-end'}>
                {total} {intl.formatMessage(messages.membersTotal)}
              </Wrap>
            </Wrap>
          </Wrap>
          <Wrap justifyCont={'flex-end'}>
            <StyledButton icon onClick={() => showGroupDetails(group)}>
              <PencilIcon color={accentColorPrim} width={'1.8rem'} />
            </StyledButton>
          </Wrap>
        </Wrap>
        <Wrap
          width={'100%'}
          justifyCont={'space-between'}
          alignItems={'center'}
          margin={'2rem 0'}
        >
          <Wrap alignItems={'center'}>
            {subscriptionPlan?.questionnaireAbility &&
              !!groupMembers.filter((m) => m.checked).length && (
                <Button secondary onClick={() => setShowSend(true)}>
                  {intl.formatMessage(messages.sendQuestion)}
                </Button>
              )}
          </Wrap>
          <Wrap>
            {/*Button icon>
              <AccessCodeIcon color={accentColorPrim} width={'1.8rem'} />
            </Button>*/}
            <Tooltip
              title={intl.formatMessage(messages.addMembers)}
              placement="top"
            >
              <div>
                <Button icon onClick={() => setAddMembersDialog(true)}>
                  <AddUserIcon color={accentColorPrim} width={'1.8rem'} />
                </Button>
              </div>
            </Tooltip>
          </Wrap>
        </Wrap>
        <Wrap grow={'1'} overflow={'hidden'} width={'100%'} hiddenXS>
          <StyledTable
            selectedField="checked"
            data={groupMembers.slice(skip, skip + pageSize)}
            onPageChange={pageHandler}
            scrollable={'virtual'}
            skip={skip}
            pageSize={pageSize}
            total={total}
            rowHeight={55}
            onSelectionChange={(e) => updateField(e.dataItem, 'checked')}
            onRowClick={(e) => updateField(e.dataItem, 'checked')}
          >
            <GridColumn
              field={'checked'}
              width="50px"
              headerCell={() => <div />}
            />
            <GridColumn
              field="firstName"
              title={intl.formatMessage(messages.fName)}
            />
            <GridColumn
              field="lastName"
              title={intl.formatMessage(messages.lName)}
            />
            <GridColumn
              field="gender"
              title={intl.formatMessage(messages.sex)}
              cell={(props) => (
                <td>
                  {props.dataItem.gender === Gender.Male
                    ? intl.formatMessage(messages.male)
                    : intl.formatMessage(messages.female)}
                </td>
              )}
            />
            <GridColumn
              field="birth"
              title={intl.formatMessage(messages.birth)}
              cell={(props) => (
                <td>
                  <Moment format={'DD MMM YYYY'}>
                    {props.dataItem.birthDate}
                  </Moment>
                </td>
              )}
            />
            <GridColumn
              width={'100px'}
              cell={(props) => (
                <StyledTD>
                  <Wrap alignItems={'center'}>
                    <Button icon transparent>
                      <ReportIcon color={accentColorPrim} width={'2rem'} />
                    </Button>
                    <Button
                      icon
                      transparent
                      onClick={() => handleRemoveMember(props.dataItem.id)}
                    >
                      <CloseIcon color={red} width={'2rem'} />
                    </Button>
                  </Wrap>
                </StyledTD>
              )}
            />
          </StyledTable>
        </Wrap>
        <Wrap grow={'1'} overflow={'hidden'} width={'100%'} hiddenSM>
          <StyledTable
            selectedField="checked"
            data={groupMembers.slice(skip, skip + pageSize)}
            onPageChange={pageHandler}
            scrollable={'virtual'}
            skip={skip}
            pageSize={pageSize}
            total={total}
            rowHeight={55}
            onSelectionChange={(e) => updateField(e.dataItem, 'checked')}
            onRowClick={(e) => updateField(e.dataItem, 'checked')}
          >
            <GridColumn
              field={'checked'}
              width="50px"
              headerCell={() => <div />}
            />
            <GridColumn
              cell={(props) => (
                <td>
                  {props.dataItem.firstName}{' '}{props.dataItem.lastName}
                </td>
              )}
            />
          </StyledTable>
        </Wrap>
      </Wrap>
      {addMembers && (
        <DialogWrap
          children={
            <AddMember
              addedMembers={groupMembers}
              onCancel={() => setAddMembersDialog(false)}
              onSave={handleAddMembers}
            />
          }
        />
      )}
      {showSend && (
        <DialogWrap
          children={
            <SendQuestionnaires
              onCancel={() => setShowSend(false)}
              onSave={sendQuestionnaires}
            />
          }
        />
      )}
    </>
  );
};

export default memo(GroupDetails);
